/**
 * Payload schemas for the support chat and the admin dashboard's socket events (harness to nine
 * B5c, P1 item 3, third batch). Each is written from what its producers send today: the web's
 * support store and composer, the dashboard's support store and socket hook, and the server's
 * support handlers, Slack bridge, admin routes and issue tracker (every emit site is named on its
 * schema). The rows are database rows (`select('*')`), so a row schema follows the table.
 *
 * Many support events are request/ack: the schema describes the payload a client sends, not the
 * ack. An ack-only event (the client passes just the callback) has no payload: `z.object({})
 * .strict()`, optional.
 *
 * The support types in types/support.ts are DERIVED from these schemas (one author).
 */
import { z } from 'zod';

/** A request/ack event whose client sends only the callback (no payload). */
export const SupportAckOnlyPayloadSchema = z.object({}).strict().optional();

// ============================================================================
// Support rows
// ============================================================================

/** A `support_conversations` row (types/support.ts SupportConversation derives from this). */
export const SupportConversationSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  assigned_agent_id: z.string().nullable(),
  status: z.enum(['ai_handling', 'waiting', 'active', 'resolved']),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
  subject: z.string().nullable(),
  page_url: z.string().nullable(),
  user_agent: z.string().nullable(),
  first_response_at: z.string().nullable(),
  resolved_at: z.string().nullable(),
  resolved_by: z.enum(['ai', 'agent', 'user']).nullable(),
  csat_rating: z.number().nullable(),
  /** Slack bridge: channel + root message ts of this ticket's thread (null until first message posts). */
  slack_channel_id: z.string().nullable(),
  slack_thread_ts: z.string().nullable(),
  /** Concierge: the task this ticket is about (chosen in the composer or via error deep link). */
  linked_task_id: z.string().nullable(),
  /** First moment anyone on the team saw this ticket — powers "With our team, being looked at". */
  team_seen_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** One attachment on a support message (types/support.ts SupportAttachment derives from this). */
export const SupportAttachmentSchema = z.object({
  /** Storage object path inside the private support-attachments bucket. */
  url: z.string(),
  filename: z.string(),
  mime_type: z.string(),
  size: z.number(),
  /** Short-lived signed view URL, minted server-side at read time. Never persisted. */
  signed_url: z.string().nullable().optional(),
});

/**
 * A `support_messages` row as the server sends it: `sender_name` and `sender_avatar_url` are added
 * at read time, and attachments carry a signed view url (types/support.ts SupportMessage derives
 * from this). `metadata` is `{}` or, from the Slack bridge, `{ via: 'slack', ... }`, or from a
 * Composer report `{ source: 'composer', ... }`.
 */
export const SupportMessageSchema = z.object({
  id: z.string(),
  conversation_id: z.string(),
  sender_type: z.enum(['user', 'ai_bot', 'agent']),
  sender_id: z.string().nullable(),
  sender_name: z.string().nullable(),
  sender_avatar_url: z.string().nullable(),
  content: z.string(),
  is_internal_note: z.boolean(),
  attachments: z.array(SupportAttachmentSchema),
  metadata: z.record(z.string(), z.unknown()),
  read_at: z.string().nullable(),
  created_at: z.string(),
});

// ============================================================================
// Support, client → server
// ============================================================================

/**
 * support:conversation:open: the web's new ticket (`{ page_url?, task_id? }`, possibly `{}`) or an
 * existing one (`{ conversation_id }`); the dashboard opens one by id or for a person (`user_id`).
 */
export const SupportConversationOpenPayloadSchema = z.object({
  conversation_id: z.string().optional(),
  page_url: z.string().optional(),
  /** Admin-initiated: create a conversation for this user. */
  user_id: z.string().optional(),
  /** Link the new ticket to one of the user's tasks (composer picker / error deep link). */
  task_id: z.string().optional(),
});

/**
 * support:message:send (the web: attachments always sent, possibly `[]`, without `signed_url`) and
 * support:reply:send (the dashboard: never sends attachments).
 */
export const SupportMessagePayloadSchema = z.object({
  conversation_id: z.string(),
  content: z.string(),
  attachments: z.array(SupportAttachmentSchema).optional(),
});

/** support:note:send (the dashboard; the content is trimmed). */
export const SupportNotePayloadSchema = z.object({
  conversation_id: z.string(),
  content: z.string(),
});

/** support:assign (the dashboard; the only UI caller unassigns with `null`). */
export const SupportAssignPayloadSchema = z.object({
  conversation_id: z.string(),
  agent_id: z.string().nullable(),
});

/** support:resolve (the dashboard) and support:customer:resolve (the web). */
export const SupportResolvePayloadSchema = z.object({
  conversation_id: z.string(),
});

/** support:priority:update (the dashboard). */
export const SupportPriorityPayloadSchema = z.object({
  conversation_id: z.string(),
  priority: SupportConversationSchema.shape.priority,
});

/**
 * support:typing:start / :stop: the web's ComposerDesk sends `{ conversation_id }`; the server echoes
 * it to the conversation's room under the same name with the typist's `user_id` (no client listens
 * to the echo today).
 */
export const SupportTypingPayloadSchema = z.object({
  conversation_id: z.string(),
  user_id: z.string().optional(),
});

/** support:article:search (the web's support store; no UI calls it today). */
export const SupportArticleSearchPayloadSchema = z.object({
  query: z.string(),
});

/** support:attachment:sign (the web, before an upload). */
export const SupportAttachmentSignPayloadSchema = z.object({
  filename: z.string(),
  mime_type: z.string(),
  size: z.number(),
});

/** support:rate (the web; the UI taps 1..5, the server rounds and range-checks). */
export const SupportRatePayloadSchema = z.object({
  conversation_id: z.string(),
  /** 1–5, the "Did we take good care of you?" tap. */
  rating: z.number(),
});

/** support:link_task: link (or relink) one of the user's tasks to an open ticket, mid-thread. */
export const SupportLinkTaskPayloadSchema = z.object({
  conversation_id: z.string(),
  task_id: z.string(),
});

// ============================================================================
// Support, server → clients
// ============================================================================

/**
 * support:message:received (to the conversation, its owner and the inbox) and support:message:new
 * (the inbox only): handlers/socket/support-handlers.ts, the Slack bridge and the Composer report.
 * The socket handlers read the conversation back after writing the message, and send `null` when
 * that read fails.
 */
export const SupportMessageReceivedPayloadSchema = z.object({
  message: SupportMessageSchema,
  conversation: SupportConversationSchema.nullable(),
});

/** support:conversation:update, support:new_conversation and support:agent:assigned. */
export const SupportConversationUpdatePayloadSchema = z.object({
  conversation: SupportConversationSchema,
});

/** support:messages:read (support-handlers.ts, on an open and on a team reply). */
export const SupportMessagesReadPayloadSchema = z.object({
  conversation_id: z.string(),
  read_at: z.string(),
  /**
   * Who did the reading. 'team' = someone on our side opened or answered the
   * ticket (powers "Seen by our team"); 'user' = the owner caught up on
   * replies. Clients must only mark their own outgoing messages seen when the
   * OTHER side read them.
   */
  reader: z.enum(['user', 'team']),
});

// ============================================================================
// Admin dashboard
// ============================================================================

/** admin:health:subscribe (the dashboard's socket hook): join the admin room. Sent with no payload. */
export const AdminHealthSubscribePayloadSchema = z.object({}).strict().optional();

/**
 * admin:error:event (sandbox-relay.service.ts through emitToAdminRoom, which stamps `timestamp`):
 * an agent error (`taskId` may be absent) or a credit wall.
 */
export const AdminErrorEventPayloadSchema = z.object({
  userId: z.string(),
  taskId: z.string().optional(),
  errorType: z.enum(['agent_error', 'credit_exhausted']),
  errorMessage: z.string(),
  timestamp: z.string(),
});

/** admin:message:received (handlers/admin-routes.handler.ts, the in-app channel; no timestamp). */
export const AdminMessageReceivedPayloadSchema = z.object({
  id: z.string(),
  subject: z.string().nullable(),
  content: z.string(),
  from_admin_id: z.string(),
  created_at: z.string(),
});

/** An `admin_issues` row (apps/server issue.service.ts IssueRow); `description` and `attachments` are nullable columns. */
export const AdminIssueSchema = z.object({
  id: z.string(),
  identifier: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  type: z.enum(['bug', 'feature', 'improvement', 'task', 'chore']),
  status: z.enum(['backlog', 'todo', 'in_progress', 'done', 'cancelled']),
  /** 0–4 (a CHECK constraint). */
  priority: z.number(),
  creator_id: z.string(),
  assignee_id: z.string().nullable(),
  labels: z.array(z.string()),
  /** A `date` column: `YYYY-MM-DD`. */
  due_date: z.string().nullable(),
  attachments: z.array(z.object({ url: z.string(), filename: z.string(), size: z.number(), storagePath: z.string() })).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** An `admin_issue_comments` row (apps/server issue.service.ts IssueCommentRow). */
export const AdminIssueCommentSchema = z.object({
  id: z.string(),
  issue_id: z.string(),
  parent_id: z.string().nullable(),
  author_id: z.string(),
  content: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

// handlers/admin-issues.handler.ts, every one through emitToAdminRoom (which stamps `timestamp`).

/** admin:issue:created: the raw row (no creator name or avatar, attachment urls not re-signed). */
export const AdminIssueCreatedPayloadSchema = z.object({
  issue: AdminIssueSchema,
  timestamp: z.string(),
});

/** admin:issue:updated: `updatedBy` is the admin's email. */
export const AdminIssueUpdatedPayloadSchema = z.object({
  id: z.string(),
  issue: AdminIssueSchema,
  updatedBy: z.string(),
  timestamp: z.string(),
});

/** admin:issue:deleted. */
export const AdminIssueDeletedPayloadSchema = z.object({
  id: z.string(),
  timestamp: z.string(),
});

/** admin:issue:comment:added. */
export const AdminIssueCommentAddedPayloadSchema = z.object({
  issueId: z.string(),
  comment: AdminIssueCommentSchema,
  timestamp: z.string(),
});

/** admin:issue:comment:updated: `issueId` is the body's `issue_id`, else the comment's own. */
export const AdminIssueCommentUpdatedPayloadSchema = z.object({
  commentId: z.string(),
  issueId: z.string(),
  content: z.string(),
  timestamp: z.string(),
});

/**
 * admin:issue:comment:deleted: `issueId` is the request's `issueId` query (what the dashboard
 * sends; `issue_id` is still accepted), else null.
 */
export const AdminIssueCommentDeletedPayloadSchema = z.object({
  commentId: z.string(),
  issueId: z.string().nullable(),
  timestamp: z.string(),
});
